import { useState, useEffect } from 'react'
import dayjs from 'dayjs'
import { getOverallRosterData } from 'src/supportPortal/services/rosterManagementService'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

export function useOverallRosterData({
  fromDate,
  toDate,
  clinicBrandIds = null,
  chamberRoomIds = null,
  specializationIds = null, // Legacy parameter
  targetSpecialitySlugs = null, // New parameter - preferred
  eventTypes = null,
  mentorId = null,
  includeUnoccupied = true,
  includeBookedSlots = true,
  debug: debugParam = false,
}) {
  const [rosterData, setRosterData] = useState([])
  const [summary, setSummary] = useState(null)
  const [unoccupiedSlots, setUnoccupiedSlots] = useState([])
  const [debugInfo, setDebugInfo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [refetchTrigger, setRefetchTrigger] = useState(0)

  // Automatically enable debug in development mode, but never in production
  // In production builds, debug is always false regardless of the parameter
  const isProduction = import.meta.env.PROD || import.meta.env.MODE === 'production'
  const effectiveDebug = isProduction ? false : true // Always enabled in dev, always disabled in prod

  useEffect(() => {
    async function fetchData() {
      if (!fromDate || !toDate) return

      // If mentorId is provided, use the practitioner-specific API
      if (mentorId) {
        setLoading(true)
        setError(null)
        try {
          const { getMyRoster } = await import('src/supportPortal/services/rosterManagementService')
          const resp = await getMyRoster({
            fromDate: dayjs(fromDate).format('YYYY-MM-DD'),
            toDate: dayjs(toDate).format('YYYY-MM-DD'),
            mentorId,
            includeBookedSlots,
          })
          if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
            const data = getDataMapFromCommonResponse(resp)
            setRosterData(data.rosterSlots || [])
            setSummary(data.summary || null)
            setUnoccupiedSlots([])
          } else {
            setError('Failed to fetch roster data')
          }
        } catch (err) {
          setError(err.message || 'Failed to fetch roster data')
        } finally {
          setLoading(false)
        }
        return
      }

      setLoading(true)
      setError(null)

      try {
        const resp = await getOverallRosterData({
          fromDate: dayjs(fromDate).format('YYYY-MM-DD'),
          toDate: dayjs(toDate).format('YYYY-MM-DD'),
          clinicBrandIds,
          chamberRoomIds,
          specializationIds, // Legacy - kept for backward compatibility
          targetSpecialitySlugs, // New - preferred
          eventTypes,
          includeUnoccupied,
          includeBookedSlots,
          debug: effectiveDebug,
        })

        if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          const data = getDataMapFromCommonResponse(resp)
          setRosterData(data.rosterSlots || [])
          setSummary(data.summary || null)
          setUnoccupiedSlots(data.unoccupiedSlots || [])
          // Debug information is available when debug=true parameter is used
          if (data.debug) {
            setDebugInfo(data.debug)
          } else {
            setDebugInfo(null)
          }
        } else {
          setError('Failed to fetch roster data')
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch roster data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [fromDate, toDate, clinicBrandIds, chamberRoomIds, specializationIds, targetSpecialitySlugs, eventTypes, mentorId, includeUnoccupied, includeBookedSlots, effectiveDebug, refetchTrigger])

  const refetch = () => {
    // Trigger refetch by incrementing the trigger counter
    setRefetchTrigger((prev) => prev + 1)
  }

  return {
    rosterData,
    summary,
    unoccupiedSlots,
    debug: debugInfo, // Debug information when debug=true (shows filtering, scope, timezone warnings)
    loading,
    error,
    refetch,
  }
}
